import * as Dialog from '@radix-ui/react-dialog';
import { forwardRef, type ReactNode } from 'react';
import { Icon } from './icons';
import { cx, humanize, statusTone } from '../utils';

export function Badge({
  children,
  tone = 'neutral',
}: {
  children: ReactNode;
  tone?: string;
}): React.JSX.Element {
  return <span className={cx('badge', `badge--${tone}`)}>{children}</span>;
}

export function StatusBadge({ status }: { status: string | undefined }): React.JSX.Element {
  return <Badge tone={statusTone(status)}>{humanize(status)}</Badge>;
}

export const Button = forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    variant?: 'default' | 'primary' | 'quiet' | 'danger';
    icon?: React.ComponentProps<typeof Icon>['name'];
  }
>(function Button({ className, variant = 'default', icon, children, ...props }, ref) {
  return (
    <button ref={ref} className={cx('button', `button--${variant}`, className)} {...props}>
      {icon ? <Icon name={icon} size={16} /> : null}
      {children}
    </button>
  );
});

export function Card({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>): React.JSX.Element {
  return (
    <div className={cx('card', className)} {...props}>
      {children}
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: ReactNode;
}): React.JSX.Element {
  return (
    <div className="empty-state">
      <span className="empty-state__mark">
        <Icon name="atlas" size={22} />
      </span>
      <h3>{title}</h3>
      <p>{description}</p>
      {action}
    </div>
  );
}

export function LoadingBlock({
  label = 'Loading workspace',
}: {
  label?: string;
}): React.JSX.Element {
  return (
    <div className="loading-block" role="status">
      <span className="spinner" />
      <span>{label}</span>
    </div>
  );
}

export function ErrorBlock({
  error,
  onRetry,
}: {
  error: unknown;
  onRetry?: () => void;
}): React.JSX.Element {
  const message = error instanceof Error ? error.message : 'The request could not be completed.';
  return (
    <Card className="error-block" role="alert">
      <Icon name="warning" />
      <div>
        <strong>Unable to load this view</strong>
        <p>{message}</p>
      </div>
      {onRetry ? (
        <Button variant="quiet" onClick={onRetry}>
          Try again
        </Button>
      ) : null}
    </Card>
  );
}

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: string;
  title: string;
  description: string;
  actions?: ReactNode;
}): React.JSX.Element {
  return (
    <header className="page-header">
      <div>
        {eyebrow ? <p className="eyebrow">{eyebrow}</p> : null}
        <h1>{title}</h1>
        <p className="page-header__description">{description}</p>
      </div>
      {actions ? <div className="page-header__actions">{actions}</div> : null}
    </header>
  );
}

export function SectionHeader({
  eyebrow,
  title,
  description,
  action,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  action?: ReactNode;
}): React.JSX.Element {
  return (
    <div className="section-header">
      <div>
        {eyebrow ? <p className="eyebrow">{eyebrow}</p> : null}
        <h2>{title}</h2>
        {description ? <p>{description}</p> : null}
      </div>
      {action}
    </div>
  );
}

export function Modal({
  open,
  onOpenChange,
  title,
  description,
  children,
  wide = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  wide?: boolean;
}): React.JSX.Element {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay" />
        <Dialog.Content className={cx('dialog-content', wide && 'dialog-content--wide')}>
          <div className="dialog-heading">
            <div>
              <Dialog.Title>{title}</Dialog.Title>
              {description ? <Dialog.Description>{description}</Dialog.Description> : null}
            </div>
            <Dialog.Close asChild>
              <Button variant="quiet" className="icon-button" aria-label="Close dialog">
                <Icon name="close" />
              </Button>
            </Dialog.Close>
          </div>
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function Field({
  label,
  hint,
  children,
  className,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
  className?: string;
}): React.JSX.Element {
  return (
    <label className={cx('field', className)}>
      <span className="field__label">{label}</span>
      {children}
      {hint ? <span className="field__hint">{hint}</span> : null}
    </label>
  );
}

export function Stat({
  label,
  value,
  meta,
  tone,
}: {
  label: string;
  value: ReactNode;
  meta?: ReactNode;
  tone?: string;
}): React.JSX.Element {
  return (
    <div className={cx('stat', tone && `stat--${tone}`)}>
      <span className="stat__label">{label}</span>
      <strong>{value}</strong>
      {meta ? <span className="stat__meta">{meta}</span> : null}
    </div>
  );
}
